import { useEffect, useState } from 'react'
import { useQueryClient } from 'react-query'
import { Form, useField, useFormikContext } from 'formik'
import styled from 'styled-components'
import { Button, Checkbox, Divider, P, Wrapper } from '@farewill/ui'
import { COLOR, FONT, GTR } from '@farewill/ui/tokens'

import SelectInput from 'components/form/select-input'
import FilterPanelWrapper from 'components/list/filter-panel-wrapper'
import PartnerSelect from 'components/partner-select'
import Switch from 'components/switch'
import { AdminUser } from 'lib/models/admin-user'
import { Constants } from 'lib/models/constants'
import { isOption } from 'lib/types/isOption'
import {
  FAREWILL_PRODUCTS_OPTIONS,
  PRODUCTS,
  FUNERAL_BLOCKED_REASONS_OPTIONS,
  FUNERAL_LEAD_STATUS,
  LEAD_PARTNER_TYPES,
  LEAD_SOURCE_IDENTIFIER_OPTIONS,
  LEAD_SOURCE_TYPE_OPTIONS,
  LEAD_STATUS,
  LPA_UNSUITABLE_REASONS_OPTIONS,
} from 'utils/enums'
import {
  showFuneralBlockedReasonSelect,
  showPartnerSelect,
  showUnsuitableReasonSelect,
} from 'utils/helpers'

import LastActivityFilter from './last-activity-filter'
import GroupedCheckbox from 'components/grouped-checkbox'
import BrandFilter from '../case-list/filter-panel/shared/brand-filter'

const StyledFilterSwitch = styled(Wrapper)`
  display: flex;
  gap: ${GTR.XS};
`

const FilterHeaders = styled(Wrapper)`
  display: flex;
  align-items: center;
  margin-bottom: ${GTR.S};
  padding-bottom: ${GTR.XS};
  position: relative;
`

const StyledFilter = styled(Wrapper)`
  margin-bottom: ${GTR.L};
`

const StyledOwnerHeader = styled(Wrapper)`
  display: flex;
  justify-content: space-between;
`

const StyledFilterButton = styled(Button.Underline)`
  padding: 0;
  font-size: ${GTR.S};
`

const StyledPersistFiltersButton = styled(Button.Plain)<{
  $isActive: boolean
}>`
  padding: 0;

  span {
    font-weight: ${FONT.WEIGHT.REGULAR};
    font-size: ${FONT.SIZE.S};
  }
`

const FocusCallsTodayCheckbox = () => {
  const [field] = useField({ name: 'callToday', type: 'checkbox' })
  return <StyledCheckbox label="Call scheduled today" size="S" {...field} />
}

const MissedLastCallCheckbox = () => {
  const [field] = useField({ name: 'missedLastCall', type: 'checkbox' })
  return <StyledCheckbox label="Missed last call" size="S" {...field} />
}

const LastChanceToConnectCheckbox = () => {
  const [field] = useField({ name: 'lastChance', type: 'checkbox' })
  return <StyledCheckbox label="Last chance to connect" size="S" {...field} />
}

const TimeSelectedCheckbox = () => {
  const [field] = useField({
    name: 'scheduledNextCallAtTimeSet',
    type: 'checkbox',
  })
  return <StyledCheckbox label="Time selected" size="S" {...field} />
}

const GuidePostedCheckbox = () => {
  const [field] = useField({ name: 'funeralPlanGuideSent', type: 'checkbox' })
  return <StyledCheckbox label="Postal guide requested" size="S" {...field} />
}

type FilterPanelProps = {
  loggedInAdminUserId: number
  storageEnabled: boolean
  toggleStorageEnabled: () => void
}

type Reason = {
  label: string
  value: string
}

export type Filters = {
  adminUserIds: string[]
  partnerId: string
  products: string[]
  brandIds: string[]
  sourceIdentifier: string
  origin: string
  status: string
  unsuitableReason: string
  blockedReason: string
  callToday: boolean
  missedLastCall: boolean
  lastChance: boolean
  scheduledNextCallAtTimeSet: boolean
  funeralPlanGuideSent: boolean
  lastActivityAt: {
    lte: string
    gte: string
    option: string
  }
}

const StyledCheckbox = styled(Checkbox)`
  margin: ${GTR.S} 0 !important;
`

const FilterPanel = ({
  loggedInAdminUserId,
  storageEnabled,
  toggleStorageEnabled,
}: FilterPanelProps) => {
  const cache = useQueryClient()
  const constants = cache.getQueryData('constants') as Constants
  const leadUnsuitableReasonsOptions =
    constants?.attributes?.leadUnsuitableReasonsOptions
  const adminUsers = cache.getQueryData<AdminUser[]>('adminUsers')
  const { setFieldValue, handleSubmit, values } = useFormikContext<Filters>()
  const [unsuitableReasons, setUnsuitableReasons] = useState<Reason[]>([])
  const blockedReasons = FUNERAL_BLOCKED_REASONS_OPTIONS

  // Reset page when filters or sort are changed before submitting
  useEffect(() => {
    handleSubmit()
  }, [handleSubmit, values])

  // Clear funeralPlanGuideSent when funeral plan is no longer in scope
  useEffect(() => {
    const funeralPlanInScope =
      values.products.length === 1 &&
      values.products.includes(PRODUCTS.FUNERAL_PLAN)
    if (!funeralPlanInScope && values.funeralPlanGuideSent) {
      setFieldValue('funeralPlanGuideSent', false)
    }
  }, [values.products, values.funeralPlanGuideSent, setFieldValue])

  useEffect(() => {
    const selectedProducts = values?.products // Array of selected products
    let allProducts
    // If there are no selectedProducts, we define allProducts
    if (!selectedProducts || selectedProducts.length === 0) {
      allProducts = Object.values(PRODUCTS)
    }
    const products = allProducts ?? selectedProducts

    const funeralUnsuitableReasonsOptions =
      leadUnsuitableReasonsOptions?.funeral ?? []
    const funeralPlanUnsuitableReasonsOptions =
      leadUnsuitableReasonsOptions?.funeral_plan ?? []
    const probateUnsuitableReasonsOptions =
      leadUnsuitableReasonsOptions?.probate ?? []
    const willUnsuitableReasonsOptions =
      leadUnsuitableReasonsOptions?.will ?? []

    const unsuitableReasonsOptions = [] as Reason[]
    products.forEach((product) => {
      switch (product) {
        case PRODUCTS.WILL:
          return unsuitableReasonsOptions.push(...willUnsuitableReasonsOptions)

        case PRODUCTS.PROBATE:
          return unsuitableReasonsOptions.push(
            ...probateUnsuitableReasonsOptions
          )

        case PRODUCTS.FUNERAL:
          return unsuitableReasonsOptions.push(
            ...funeralUnsuitableReasonsOptions
          )

        case PRODUCTS.LPA:
          return unsuitableReasonsOptions.push(
            ...LPA_UNSUITABLE_REASONS_OPTIONS
          )

        case PRODUCTS.FUNERAL_PLAN:
          return unsuitableReasonsOptions.push(
            ...funeralPlanUnsuitableReasonsOptions
          )

        default:
          return unsuitableReasonsOptions.push(...[])
      }
    })

    // Get an array of unique values (duplicate values are removed)
    const uniqueUnsuitableReasonsOptionsValues = Array.from(
      new Set(unsuitableReasonsOptions.map((reason) => reason.value))
    )

    // Get an array of unique options
    const uniqueOptions = uniqueUnsuitableReasonsOptionsValues.map((value) => {
      return unsuitableReasonsOptions.find((option) => option.value === value)
    })

    setUnsuitableReasons(uniqueOptions as Reason[])
  }, [values?.products, setUnsuitableReasons, leadUnsuitableReasonsOptions])

  const sourceIdentifierOptions =
    LEAD_SOURCE_IDENTIFIER_OPTIONS[
      values.origin as keyof typeof LEAD_SOURCE_IDENTIFIER_OPTIONS
    ]

  const originOptions = LEAD_SOURCE_TYPE_OPTIONS.concat(
    { value: 'd2c', label: 'D2C' },
    { value: LEAD_PARTNER_TYPES.charity, label: 'Charity' },
    { value: LEAD_PARTNER_TYPES.other_partner, label: 'Partner' }
  )

  const adminUserOptions = adminUsers
    ?.filter(({ id }) => id !== loggedInAdminUserId)
    .map((user) => ({
      value: String(user.id),
      label: user.attributes.name,
    }))

  adminUserOptions?.unshift(
    { label: 'My leads', value: String(loggedInAdminUserId) },
    { label: 'Unassigned', value: 'null' }
  )

  const clearFields = (fields: string[]) =>
    fields.forEach((field) => setFieldValue(field, ''))

  const handleAssignYourself = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault()
    setFieldValue('adminUserIds', [String(loggedInAdminUserId)])
  }

  return (
    <FilterPanelWrapper>
      <Form>
        <FilterHeaders>
          <P size="M" margin={0} strong>
            Filters
          </P>
        </FilterHeaders>
        <StyledFilter>
          <StyledOwnerHeader margin={[0, 0, 'S']}>
            <P strong size="S" margin={0}>
              Owner
            </P>
            <StyledFilterButton
              disabled={values.adminUserIds.includes(
                loggedInAdminUserId.toString()
              )}
              type="button"
              onClick={handleAssignYourself}
            >
              Filter by me
            </StyledFilterButton>
          </StyledOwnerHeader>
          <SelectInput
            name="adminUserIds"
            options={adminUserOptions}
            isMulti
            small
          />
        </StyledFilter>
        <StyledFilter>
          <P strong margin={[0, 0, 'XS']} size="S">
            Product
          </P>
          {FAREWILL_PRODUCTS_OPTIONS.map((product) => (
            <GroupedCheckbox
              key={`${product.value}-filter`}
              label={product.label as string}
              value={product.value as string}
              groupName="products"
            />
          ))}
        </StyledFilter>
        <StyledFilter>
          <BrandFilter />
        </StyledFilter>
        <StyledFilter>
          <P strong margin={[0, 0, 'XS']} size="S">
            Focus on
          </P>
          <FocusCallsTodayCheckbox />
          <LastChanceToConnectCheckbox />
          <TimeSelectedCheckbox />
          <MissedLastCallCheckbox />
          {values.products.length === 1 &&
            values.products.includes(PRODUCTS.FUNERAL_PLAN) && (
              <GuidePostedCheckbox />
            )}
        </StyledFilter>

        <StyledFilter>
          <P strong margin={[0, 0, 'XS']} size="S">
            Status
          </P>
          <SelectInput
            name="status"
            onChange={(e) => {
              if (!e) {
                clearFields(['status', 'unsuitableReason', 'blockedReason'])
              } else if (isOption(e) && e?.value === LEAD_STATUS.LOST) {
                setFieldValue('status', LEAD_STATUS.LOST)
              } else {
                isOption(e) && setFieldValue('status', e.value)
                clearFields(['unsuitableReason', 'blockedReason'])
              }
            }}
            options={[
              { label: 'Open', value: LEAD_STATUS.OPEN },
              { label: 'Qualified', value: LEAD_STATUS.QUALIFIED },
              { label: 'Lost', value: LEAD_STATUS.LOST },
              { label: 'Blocked', value: FUNERAL_LEAD_STATUS.BLOCKED },
            ]}
            small
            isClearable
          />
        </StyledFilter>
        {showUnsuitableReasonSelect(values.status) && (
          <StyledFilter>
            <P strong margin={[0, 0, 'XS']} size="S">
              Unsuitable reason
            </P>
            <SelectInput
              name="unsuitableReason"
              options={unsuitableReasons}
              small
              isClearable
            />
          </StyledFilter>
        )}
        {showFuneralBlockedReasonSelect(values.status) && (
          <StyledFilter>
            <P strong margin={[0, 0, 'XS']} size="S">
              Blocked reason
            </P>
            <SelectInput
              name="blockedReason"
              options={blockedReasons}
              small
              isClearable
            />
          </StyledFilter>
        )}
        <StyledFilter>
          <P strong margin={[0, 0, 'XS']} size="S">
            Origin
          </P>
          <SelectInput
            name="origin"
            onChange={(e) => {
              if (!e) {
                setFieldValue('origin', '')
                clearFields(['sourceIdentifier', 'partnerId'])
              } else if (
                e &&
                'value' in e &&
                typeof e.value === 'string' &&
                [
                  'other_partner',
                  'outbound_call',
                  'internal_referral',
                  'charity',
                ].includes(e.value)
              ) {
                setFieldValue('origin', e.value)
              } else {
                e && 'value' in e && setFieldValue('origin', e.value)
                clearFields(['sourceIdentifier', 'partnerId'])
              }
            }}
            options={originOptions}
            small
            isClearable
          />
        </StyledFilter>
        {sourceIdentifierOptions && (
          <StyledFilter>
            <P strong margin={[0, 0, 'XS']} size="S">
              Source identifier
            </P>
            <SelectInput
              name="sourceIdentifier"
              options={sourceIdentifierOptions}
              small
              isClearable
            />
          </StyledFilter>
        )}
        {showPartnerSelect(values.origin) && (
          <StyledFilter>
            <P strong margin={[0, 0, 'XS']} size="S">
              Partner name
            </P>
            <PartnerSelect
              name="partnerId"
              partnerType={values.origin}
              small
              isClearable
            />
          </StyledFilter>
        )}
        <StyledFilter>
          <P size="S" strong>
            Last activity
          </P>
          <LastActivityFilter name="lastActivityAt" />
        </StyledFilter>
        <Divider margin={['L', 0]} />
        <StyledFilterSwitch>
          <Switch
            isOn={storageEnabled}
            color={COLOR.ACCENT.SECONDARY}
            handleToggle={toggleStorageEnabled}
          />
          <StyledPersistFiltersButton onClick={toggleStorageEnabled}>
            <span>
              {storageEnabled ? 'Filters saved' : 'Filters not saved'}
            </span>
          </StyledPersistFiltersButton>
        </StyledFilterSwitch>
      </Form>
    </FilterPanelWrapper>
  )
}

export default FilterPanel
